import { Field, useFormikContext } from "formik";
import { Stack, TextField, ButtonGroup, Button, Typography, Alert, AlertTitle, InputAdornment, Switch } from "@mui/material";
import get from "lodash/get";
import Schedule from "@/components/Schedule";
import { OrganizationSettings } from "@/types";
import { Schedule as ISchedule } from "@/types/branch";
import { Cashier02Icon, ShoppingBasketDone03Icon, ZapIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import Frame from "@/components/Frame";

const DeliverySettings = () => {
  const { values, setFieldValue, errors, touched } = useFormikContext<OrganizationSettings>();

  const scheduleValue = get(values, `settings.services.delivery.schedule`, {});
  const maxDistance = Number(get(values, `settings.services.delivery.maxDistanceKm`, 0));

  return (
    <Stack spacing={3}>
      <Frame>
        <Stack spacing={3}>
          <Typography variant="h3">Cobertura de delivery</Typography>
          <TextField
            type="number"
            fullWidth
            label="Distancia máxima"
            value={maxDistance > 0 ? get(values, `settings.services.delivery.maxDistanceKm`, 0) : ""}
            disabled={get(values, `settings.services.delivery.maxDistanceKm`) === null}
            onChange={(e) => {
              const value = Number(e.target.value);
              setFieldValue(`settings.services.delivery.maxDistanceKm`, value);
            }}
            helperText={
              touched.settings?.services?.delivery?.maxDistanceKm &&
                !!errors.settings?.services?.delivery?.maxDistanceKm ?
                (errors.settings?.services?.delivery?.maxDistanceKm as string) :
                "Los clientes fuera de este rango no podrán solicitar envío"
            }
            error={touched.settings?.services?.delivery?.maxDistanceKm && !!errors.settings?.services?.delivery?.maxDistanceKm}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    km
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Switch
                      checked={get(values, `settings.services.delivery.maxDistanceKm`) !== null}
                      onChange={(e) => {
                        const checked = e.target.checked;

                        if (checked) {
                          setFieldValue(`settings.services.delivery.maxDistanceKm`, 1);
                        } else {
                          setFieldValue(`settings.services.delivery.maxDistanceKm`, null);
                        }
                      }}
                    />
                  </InputAdornment>
                ),
              }
            }}
          />
        </Stack>
      </Frame>
      <Frame>
        <Stack spacing={3}>
          <Typography variant="h3">Tarifa Mínima</Typography>
          <TextField
            type="number"
            fullWidth
            label="Tarifa Mínima"
            value={get(values, `settings.services.delivery.fee.minValue`, 0)}
            onChange={(e) => {
              setFieldValue(`settings.services.delivery.fee.minValue`, e.target.value);
            }}
            helperText="Los clientes no podrán seleccionar envío si su compra es menor a este valor"
          />
        </Stack>
      </Frame>

      <Frame>
        <Stack spacing={3}>
          <Typography variant="h3">Cálculo de tarifa</Typography>
          <ButtonGroup fullWidth variant="outlined">
            <Button
              variant={get(values, `settings.services.delivery.type`) === "manual" ? "contained" : "outlined"}
              onClick={() => setFieldValue(`settings.services.delivery.type`, "manual")}
            >
              Manual
            </Button>
            <Button
              variant={get(values, `settings.services.delivery.type`) === "fixed" ? "contained" : "outlined"}
              onClick={() => setFieldValue(`settings.services.delivery.type`, "fixed")}
            >
              Fijo
            </Button>
            <Button
              variant={get(values, `settings.services.delivery.type`) === "auto" ? "contained" : "outlined"}
              onClick={() => setFieldValue(`settings.services.delivery.type`, "auto")}
            >
              Automático
            </Button>
          </ButtonGroup>

          {get(values, `settings.services.delivery.type`) === "manual" ? (
            <Alert severity="info" icon={<HugeiconsIcon icon={Cashier02Icon} size={32} />}>
              <AlertTitle>Tarifa manual</AlertTitle>
              Tú defines el costo de envío de forma personalizada para cada pedido al momento de procesarlo.
            </Alert>
          ) : null}
          {get(values, `settings.services.delivery.type`) === "fixed" ? (
            <Stack spacing={3}>
              <Alert severity="info" icon={<HugeiconsIcon icon={ShoppingBasketDone03Icon} size={32} />}>
                <AlertTitle>Tarifa fija</AlertTitle>
                El sistema aplicará un monto fijo por cada envío, sin importar la distancia
              </Alert>
              <Field name="settings.services.delivery.fee.value">
                {({ field, meta }: any) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="Tarifa Fija"
                    error={meta.touched && !!meta.error}
                    helperText={meta.touched && meta.error ? (meta.error as string) : "Monto fijo que se cobrará por cada envío, sin importar la distancia"}
                  />
                )}
              </Field>
            </Stack>
          ) : null}
          {get(values, `settings.services.delivery.type`) === "auto" ? (
            <Stack spacing={3}>
              <Alert severity="info" icon={<HugeiconsIcon icon={ZapIcon} size={32} />}>
                <AlertTitle>Cálculo automático de tarifa</AlertTitle>
                El sistema calculará el costo del envío automáticamente según la distancia entre tu local y la dirección del cliente
              </Alert>
              <Stack direction="row" spacing={3}>
                <Field name="settings.services.delivery.fee.baseRate">
                  {({ field, meta }: any) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="number"
                      label="Tarifa base"
                      error={meta.touched && !!meta.error}
                      helperText={meta.touched && meta.error ? (meta.error as string) : "Se usará si el costo por kilómetro es menor a este valor"}
                    />
                  )}
                </Field>
                <Field name="settings.services.delivery.fee.valuePerDistance">
                  {({ field, meta }: any) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="number"
                      label="Tarifa por kilómetro"
                      error={meta.touched && !!meta.error}
                      helperText={meta.touched && meta.error ? (meta.error as string) : "Se sumará al total si supera la tarifa base"}
                    />
                  )}
                </Field>
              </Stack>
            </Stack>
          ) : null}
        </Stack>
      </Frame>

      <Frame>
        <Stack spacing={3}>
          <Typography variant="h3">Días y horarios de operación</Typography>
          <Schedule
            schedule={scheduleValue as ISchedule}
            onChange={(value) => {
              setFieldValue(`settings.services.delivery.schedule`, value);
            }}
          />
        </Stack>
      </Frame>
    </Stack >
  );
};
export default DeliverySettings;
